import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';

// ✅ Verificação crítica: garantir que React está disponível
if (typeof React === 'undefined' || React === null) {
  console.error('CRITICAL: React is not available in ErrorBoundary');
  throw new Error('React module not loaded - cannot render ErrorBoundary');
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // ✅ Log detalhado para debugging
    console.error('Component stack:', errorInfo.componentStack);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    
    this.setState({ error, errorInfo });
    
    // ✅ Enviar para telemetria se disponível
    if (typeof window !== 'undefined' && (window as any).datadogRum) {
      try {
        (window as any).datadogRum.addError(error, {
          context: 'ErrorBoundary',
          componentStack: errorInfo.componentStack
        });
      } catch (telemetryError) {
        console.error('Failed to send error to telemetry:', telemetryError);
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white/5 border border-white/10 rounded-lg p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">
              Erro na Aplicação
            </h1>
            <p className="text-white/70 mb-6">
              Ocorreu um erro inesperado. Tente recarregar a página.
            </p>
            
            {this.state.error && (
              <>
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3 mb-4 text-left">
                  <p className="text-red-400 text-sm font-mono break-all">
                    {this.state.error.message}
                  </p>
                </div>
                
                {/* ✅ Stack trace em desenvolvimento */}
                {import.meta.env.DEV && this.state.error.stack && (
                  <details className="mb-4 text-left">
                    <summary className="text-white/70 text-sm cursor-pointer hover:text-white transition-colors">
                      Ver detalhes técnicos
                    </summary>
                    <pre className="mt-2 text-xs text-red-300 bg-black/50 p-2 rounded overflow-auto max-h-40">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </>
            )}
            
            <Button 
              onClick={this.handleRetry}
              className="bg-luxury-red hover:bg-luxury-red/80 text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Recarregar Página
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
